import { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Check, Clock3, ShieldCheck, UserCheck, UserX } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const userStatusLabels = { pendente: 'Pendente', ativo: 'Ativo', rejeitado: 'Rejeitado', suspenso: 'Suspenso' }
const institutionStatusLabels = { pendente: 'Pendente', ativa: 'Ativa', rejeitada: 'Rejeitada', suspensa: 'Suspensa' }
const statusStyles = {
  pendente: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  ativo: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  ativa: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  rejeitado: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  rejeitada: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  suspenso: 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300',
  suspensa: 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300'
}

const filterButton = active => `whitespace-nowrap rounded-lg border px-3 py-2 text-xs font-medium ${
  active
    ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
    : 'border-gray-200 bg-white text-gray-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
}`

function AdminPage() {
  const queryClient = useQueryClient()
  const usuarioAtual = useAuthStore(state => state.usuario)
  const [filtro, setFiltro] = useState('pendente')
  const [filtroInstituicao, setFiltroInstituicao] = useState('pendente')

  const usersQuery = useQuery({
    queryKey: ['admin-usuarios'],
    queryFn: async () => (await api.get('/admin/usuarios')).data,
    enabled: usuarioAtual?.tipo_perfil === 'admin'
  })
  const institutionsQuery = useQuery({
    queryKey: ['admin-instituicoes'],
    queryFn: async () => (await api.get('/admin/instituicoes')).data,
    enabled: usuarioAtual?.tipo_perfil === 'admin'
  })

  const atualizarUsuario = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/usuarios/${id}/status`, { status }),
    onSuccess: response => {
      toast.success(response.data.message)
      queryClient.invalidateQueries({ queryKey: ['admin-usuarios'] })
    },
    onError: error => toast.error(getApiError(error))
  })
  const atualizarInstituicao = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/instituicoes/${id}/status`, { status }),
    onSuccess: response => {
      toast.success(response.data.message)
      queryClient.invalidateQueries({ queryKey: ['admin-instituicoes'] })
    },
    onError: error => toast.error(getApiError(error))
  })

  const usuarios = useMemo(() => {
    const lista = (usersQuery.data?.usuarios || []).filter(item => item.tipo_perfil !== 'admin')
    return filtro === 'todos' ? lista : lista.filter(item => item.status_cadastro === filtro)
  }, [usersQuery.data, filtro])
  const instituicoes = useMemo(() => {
    const lista = institutionsQuery.data?.instituicoes || []
    return filtroInstituicao === 'todas' ? lista : lista.filter(item => item.status === filtroInstituicao)
  }, [institutionsQuery.data, filtroInstituicao])

  if (usuarioAtual?.tipo_perfil !== 'admin') return <Navigate to="/" replace />

  const institutionFilters = [
    { id: 'pendente', label: 'Pendentes', total: institutionsQuery.data?.resumo?.pendente || 0 },
    { id: 'ativa', label: 'Ativas', total: institutionsQuery.data?.resumo?.ativa || 0 },
    { id: 'suspensa', label: 'Suspensas', total: institutionsQuery.data?.resumo?.suspensa || 0 },
    { id: 'rejeitada', label: 'Rejeitadas', total: institutionsQuery.data?.resumo?.rejeitada || 0 },
    { id: 'todas', label: 'Todas', total: institutionsQuery.data?.instituicoes?.length || 0 }
  ]
  const userFilters = [
    { id: 'pendente', label: 'Pendentes', total: usersQuery.data?.resumo?.pendente || 0 },
    { id: 'ativo', label: 'Ativos', total: usersQuery.data?.resumo?.ativo || 0 },
    { id: 'suspenso', label: 'Suspensos', total: usersQuery.data?.resumo?.suspenso || 0 },
    { id: 'rejeitado', label: 'Rejeitados', total: usersQuery.data?.resumo?.rejeitado || 0 },
    { id: 'todos', label: 'Todos', total: (usersQuery.data?.usuarios || []).filter(item => item.tipo_perfil !== 'admin').length }
  ]

  return (
    <div className="space-y-7">
      <header>
        <div className="mb-1 flex items-center gap-2"><ShieldCheck size={20} /><h1 className="text-xl font-medium lg:text-2xl">Administração</h1></div>
        <p className="text-sm text-gray-500 dark:text-zinc-400">Aprove instituições, professores e gerencie o acesso à plataforma.</p>
      </header>

      <section className="space-y-4">
        <div className="flex items-center gap-2"><Building2 size={18} /><h2 className="text-base font-medium">Instituições</h2></div>
        <div className="flex flex-wrap gap-2">
          {institutionFilters.map(item => <button key={item.id} type="button" onClick={() => setFiltroInstituicao(item.id)} className={filterButton(filtroInstituicao === item.id)}>{item.label} ({item.total})</button>)}
        </div>

        {institutionsQuery.isLoading && <p className="text-sm text-gray-500">Carregando instituições...</p>}
        {institutionsQuery.isError && <p className="card text-sm text-red-600">{getApiError(institutionsQuery.error)}</p>}
        {!institutionsQuery.isLoading && !institutionsQuery.isError && instituicoes.length === 0 && <EmptyState text="Nenhuma instituição nesta categoria." />}

        <div className="grid gap-3 xl:grid-cols-2">
          {instituicoes.map(item => (
            <article key={item.id} className="card space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-medium">{item.nome}</h3>
                  <p className="mt-0.5 text-xs capitalize text-gray-400">{item.tipo.replace('_', ' ')}</p>
                  <p className="mt-2 text-xs text-gray-500">Responsável: {item.solicitante?.nome}</p>
                  <p className="truncate text-xs text-gray-500">{item.solicitante?.email}</p>
                  {item.cnpj && <p className="mt-1 text-xs text-gray-400">CNPJ: {item.cnpj}</p>}
                </div>
                <StatusBadge status={item.status} labels={institutionStatusLabels} />
              </div>
              <div className="flex flex-wrap gap-2">
                {item.status === 'ativa' && <Link className="btn-secondary inline-flex items-center gap-1.5" to={`/instituicao?instituicao=${item.id}`}><Building2 size={14} /> Gerenciar turmas</Link>}
                {item.status !== 'ativa' && <ActionButton primary icon={UserCheck} label="Aprovar/ativar" disabled={atualizarInstituicao.isLoading} onClick={() => atualizarInstituicao.mutate({ id: item.id, status: 'ativa' })} />}
                {item.status === 'pendente' && <ActionButton icon={UserX} label="Rejeitar" disabled={atualizarInstituicao.isLoading} onClick={() => atualizarInstituicao.mutate({ id: item.id, status: 'rejeitada' })} />}
                {item.status === 'ativa' && <ActionButton icon={UserX} label="Suspender" disabled={atualizarInstituicao.isLoading} onClick={() => atualizarInstituicao.mutate({ id: item.id, status: 'suspensa' })} />}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="space-y-4 border-t border-gray-100 pt-6 dark:border-zinc-800">
        <div><h2 className="text-base font-medium">Contas de usuários</h2><p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">Aprovação e situação das contas existentes.</p></div>
        <div className="flex flex-wrap gap-2">
          {userFilters.map(item => <button key={item.id} type="button" onClick={() => setFiltro(item.id)} className={filterButton(filtro === item.id)}>{item.label} ({item.total})</button>)}
        </div>

        {usersQuery.isLoading && <p className="text-sm text-gray-500">Carregando cadastros...</p>}
        {usersQuery.isError && <p className="card text-sm text-red-600">{getApiError(usersQuery.error)}</p>}
        {!usersQuery.isLoading && !usersQuery.isError && usuarios.length === 0 && <EmptyState text="Nenhum cadastro nesta categoria." />}

        <div className="grid gap-3 xl:grid-cols-2">
          {usuarios.map(item => (
            <article key={item.id} className="card space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0"><h3 className="truncate text-sm font-medium">{item.nome}</h3><p className="truncate text-xs text-gray-500">{item.email}</p><p className="mt-1 text-xs text-gray-400">{item.tipo_perfil === 'professor' ? 'Professor' : 'Aluno'}</p></div>
                <StatusBadge status={item.status_cadastro} labels={userStatusLabels} />
              </div>
              <div className="flex flex-wrap gap-2">
                {item.status_cadastro !== 'ativo' && <ActionButton primary icon={UserCheck} label="Aprovar/ativar" disabled={atualizarUsuario.isLoading} onClick={() => atualizarUsuario.mutate({ id: item.id, status: 'ativo' })} />}
                {item.status_cadastro === 'pendente' && <ActionButton icon={UserX} label="Rejeitar" disabled={atualizarUsuario.isLoading} onClick={() => atualizarUsuario.mutate({ id: item.id, status: 'rejeitado' })} />}
                {item.status_cadastro === 'ativo' && <ActionButton icon={UserX} label="Suspender" disabled={atualizarUsuario.isLoading} onClick={() => atualizarUsuario.mutate({ id: item.id, status: 'suspenso' })} />}
                {item.status_cadastro === 'ativo' && <Check size={16} className="self-center text-emerald-600" />}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

function StatusBadge({ status, labels }) {
  return <span className={`rounded-full px-2 py-1 text-[10px] font-medium ${statusStyles[status]}`}>{labels[status]}</span>
}

function ActionButton({ primary = false, icon: Icon, label, ...props }) {
  return <button type="button" className={`${primary ? 'btn-primary' : 'btn-secondary text-red-600'} flex items-center gap-1.5`} {...props}><Icon size={14} /> {label}</button>
}

function EmptyState({ text }) {
  return <div className="card py-8 text-center"><Clock3 size={24} className="mx-auto mb-2 text-gray-300" /><p className="text-sm text-gray-500">{text}</p></div>
}

export default AdminPage
